import { StrictMode } from 'react';
import { createRoot, type Root } from 'react-dom/client';
import { IressIconProvider } from '@iress-oss/ids-components';
import { ChatApp } from '@/components/ChatApp';
import { detectClientFromEntityInfo } from '@/lib/detectClientFromEntityInfo';
import './style.css';

export const ELEMENT_NAME = 'xplan-porter';

function injectFontLink(): void {
  const FONT_ID = 'porter-fonts';
  if (document.getElementById(FONT_ID)) return;
  const link = document.createElement('link');
  link.id = FONT_ID;
  link.rel = 'stylesheet';
  // Inter for body text, Ubuntu for headings
  link.href =
    'https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&family=Ubuntu:wght@400;500;700&display=swap';
  document.head.appendChild(link);
}

class XplanPorterElement extends HTMLElement {
  private shadow: ShadowRoot | null = null;
  private lightContainer: HTMLDivElement | null = null;
  private root: Root | null = null;

  static observedAttributes: string[] = [];

  // Shadow DOM is used by default (production behaviour).
  // Set VITE_DISABLE_SHADOW_DOM=true in .env to disable it during local dev
  // so Vite's <style> tags apply directly and styling works in `pnpm dev`.
  private readonly useShadow = import.meta.env.VITE_DISABLE_SHADOW_DOM !== 'true';

  constructor() {
    super();
    if (this.useShadow) {
      // Attach shadow root immediately in the constructor so that
      // styles can be adopted before connectedCallback fires.
      this.shadow = this.attachShadow({ mode: 'open' });
    }
  }

  connectedCallback() {
    // Fonts must be loaded at the document level — they are not
    // scoped to shadow roots.
    injectFontLink();

    const container = document.createElement('div');
    container.style.cssText = 'position:relative;flex:1;min-height:0;height:100%;overflow:hidden;';

    if (this.useShadow && this.shadow) {
      // Production: render inside shadow root with adopted stylesheet.
      const sheet = (window as Window & { __porterStyleSheet?: CSSStyleSheet }).__porterStyleSheet;
      if (sheet) {
        this.shadow.adoptedStyleSheets = [sheet];
      }
      this.shadow.appendChild(container);
    } else {
      // Dev mode: render as a regular child — Vite's <style> tags in
      // document.head apply normally without a shadow boundary.
      this.lightContainer = container;
      this.appendChild(container);
    }

    this.root = createRoot(container);
    this._render();
  }

  attributeChangedCallback(_name: string, _old: string | null, _new: string | null) {
    // Re-render if the attribute changes after the element is already mounted.
    if (this.root) {
      this._render();
    }
  }

  private _render() {
    // Detect the in-view client from `window.XPLAN.apex.entityInfo` at launch.
    // Porter is launched fresh by the global-nav "Ask Porter" button (and torn
    // down on close), so this runs on every open and reflects the current
    // page's entity. A non-match returns null → ChatApp opens with no client
    // context.
    const initialClientId = detectClientFromEntityInfo();

    // Pass shadow root to IressIconProvider for proper font injection
    const iconContainer = this.useShadow && this.shadow ? this.shadow : undefined;

    this.root?.render(
      <StrictMode>
        <IressIconProvider container={iconContainer}>
          <ChatApp initialClientId={initialClientId} />
        </IressIconProvider>
      </StrictMode>,
    );
  }

  disconnectedCallback() {
    this.root?.unmount();
    this.root = null;
    if (this.useShadow && this.shadow) {
      this.shadow.innerHTML = '';
    } else if (this.lightContainer) {
      this.lightContainer.remove();
      this.lightContainer = null;
    }
  }
}

if (!customElements.get(ELEMENT_NAME)) {
  customElements.define(ELEMENT_NAME, XplanPorterElement);
}
